import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withKeys as _withKeys, createElementVNode as _createElementVNode, withDirectives as _withDirectives, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "map-search-wrapper" }
const _hoisted_2 = { class: "search-container" }
const _hoisted_3 = { class: "search-input-wrapper" }
const _hoisted_4 = {
  key: 0,
  class: "suggestions-list"
}
const _hoisted_5 = {
  key: 0,
  class: "suggestion-item loading"
}
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "icon-wrapper" }
const _hoisted_8 = { class: "suggestion-text" }
const _hoisted_9 = { class: "main-text" }
const _hoisted_10 = { class: "sub-text" }
const _hoisted_11 = { class: "history-header" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { class: "icon-wrapper history" }
const _hoisted_14 = { class: "suggestion-text" }
const _hoisted_15 = { class: "main-text" }
const _hoisted_16 = { class: "sub-text" }
const _hoisted_17 = ["onClick"]

import { ref, watch, nextTick } from "vue";
import debounce from "lodash/debounce";
import { Search, X, MapPin, History } from "lucide-vue-next";

interface NominatimResult {
	place_id: number;
	lat: string;
	lon: string;
	display_name: string;
	type: string;
	class: string;
	boundingbox?: string[];
}

const HISTORY_KEY = "map_search_history";


export default /*@__PURE__*/_defineComponent({
  __name: 'MapSearch',
  emits: ["select-location"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const query = ref("");
const suggestions = ref<NominatimResult[]>([]);
const loading = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);
const isExpanded = ref(false);

const searchHistory = ref<NominatimResult[]>([]);
function openSearch() {
	isExpanded.value = true;
	nextTick(() => {
		inputRef.value?.focus();
	});
}

function closeSearch() {
	isExpanded.value = false;
	suggestions.value = [];
}

// Init History
try {
	const saved = localStorage.getItem(HISTORY_KEY);
	if (saved) {
		searchHistory.value = JSON.parse(saved);
	}
} catch (e) {
	console.warn("Failed to load search history", e);
}

function addToHistory(item: NominatimResult) {
	searchHistory.value = searchHistory.value.filter(
		(h) => h.place_id !== item.place_id && h.display_name !== item.display_name,
	);
	searchHistory.value.unshift(item);
	if (searchHistory.value.length > 5) {
		searchHistory.value = searchHistory.value.slice(0, 5);
	}
	localStorage.setItem(HISTORY_KEY, JSON.stringify(searchHistory.value));
}

function clearHistory() {
	searchHistory.value = [];
	localStorage.removeItem(HISTORY_KEY);
}

function removeFromHistory(index: number) {
	searchHistory.value.splice(index, 1);
	localStorage.setItem(HISTORY_KEY, JSON.stringify(searchHistory.value));
}

function selectHistoryItem(item: NominatimResult) {
	selectSuggestion(item, false);
	addToHistory(item);
}

const searchNominatim = async (q: string) => {
	if (!q || q.length < 3) {
		suggestions.value = [];
		return;
	}
	loading.value = true;
	try {
		const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
			q,
		)}&limit=5&addressdetails=1`;
		const res = await fetch(url);
		if (res.ok) {
			suggestions.value = await res.json();
		}
	} catch (e) {
		console.error("Nominatim search error", e);
	} finally {
		loading.value = false;
	}
};

const debouncedSearch = debounce(searchNominatim, 500);

watch(query, (newVal) => {
	const val = newVal.trim();
	if (isCoordinates(val)) {
		suggestions.value = [];
		return;
	}
	if (!val || val.length < 3) {
		suggestions.value = [];
		return;
	}
	debouncedSearch(val);
});

function isCoordinates(text: string): boolean {
	return /^-?\d+(\.\d+)?[\s,]+-?\d+(\.\d+)?$/.test(text);
}

function handleEnter() {
	const val = query.value.trim();
	if (isCoordinates(val)) {
		const parts = val.split(/[\s,]+/);
		const lat = parseFloat(parts[0]);
		const lng = parseFloat(parts[1]);
		if (!isNaN(lat) && !isNaN(lng)) {
			emit("select-location", {
				lat,
				lng,
				label: `Coordinate: ${lat}, ${lng}`,
			});
			closeSearch();
		}
	} else if (suggestions.value.length > 0) {
		selectSuggestion(suggestions.value[0]);
	} else {
		searchNominatim(val);
	}
}

function selectSuggestion(item: NominatimResult, saveToHistory = true) {
	let bbox: [number, number, number, number] | undefined;
	if (item.boundingbox && item.boundingbox.length === 4) {
		bbox = [
			parseFloat(item.boundingbox[2]),
			parseFloat(item.boundingbox[0]),
			parseFloat(item.boundingbox[3]),
			parseFloat(item.boundingbox[1]),
		];
	}
	emit("select-location", {
		lat: parseFloat(item.lat),
		lng: parseFloat(item.lon),
		label: item.display_name,
		bbox,
	});
	query.value = item.display_name;
	closeSearch();
	if (saveToHistory) {
		addToHistory(item);
	}
}

function clearSearch() {
	query.value = "";
	suggestions.value = [];
	inputRef.value?.focus();
}

function formatMainText(item: NominatimResult) {
	const parts = item.display_name.split(",");
	return parts[0];
}
function formatSubText(item: NominatimResult) {
	const parts = item.display_name.split(",");
	if (parts.length > 1) {
		return parts.slice(1).join(",").trim();
	}
	return "";
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!isExpanded.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          class: "search-trigger-btn",
          onClick: openSearch,
          title: "Cerca un luogo"
        }, [
          _createVNode(_unref(Search), { size: 20 })
        ]))
      : _createCommentVNode("", true),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createVNode(_Transition, { name: "search-panel-fade" }, {
        default: _withCtx(() => [
          (isExpanded.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "search-panel-overlay",
                onClick: _withModifiers(closeSearch, ["self"])
              }, [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    _createVNode(_unref(Search), {
                      class: "search-icon",
                      size: 20
                    }),
                    _withDirectives(_createElementVNode("input", {
                      ref_key: "inputRef",
                      ref: inputRef,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
                      type: "text",
                      placeholder: "Cerca un luogo o coordinate (Lat, Lon)...",
                      class: "search-input",
                      onKeydown: [
                        _withKeys(handleEnter, ["enter"]),
                        _withKeys(closeSearch, ["esc"])
                      ]
                    }, null, 544), [
                      [_vModelText, query.value]
                    ]),
                    (query.value)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          class: "clear-btn",
                          onClick: clearSearch,
                          title: "Cancella"
                        }, [
                          _createVNode(_unref(X), { size: 18 })
                        ]))
                      : _createCommentVNode("", true),
                    _cache[1] || (_cache[1] = _createElementVNode("div", { class: "divider" }, null, -1)),
                    _createElementVNode("button", {
                      class: "close-panel-btn",
                      onClick: closeSearch,
                      title: "Chiudi"
                    }, [
                      _createVNode(_unref(X), { size: 20 })
                    ])
                  ]),
                  (
								suggestions.value.length > 0 ||
								loading.value ||
								(searchHistory.value.length > 0 && !query.value)
							)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                        (loading.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[2] || (_cache[2] = [
                              _createElementVNode("div", { class: "spinner" }, null, -1),
                              _createTextVNode(" Cercando... ", -1)
                            ]))]))
                          : (suggestions.value.length > 0)
                            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(suggestions.value, (item, index) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: index,
                                  class: "suggestion-item",
                                  onClick: ($event: any) => (selectSuggestion(item))
                                }, [
                                  _createElementVNode("div", _hoisted_7, [
                                    _createVNode(_unref(MapPin), { size: 18 })
                                  ]),
                                  _createElementVNode("div", _hoisted_8, [
                                    _createElementVNode("span", _hoisted_9, _toDisplayString(formatMainText(item)), 1),
                                    _createElementVNode("span", _hoisted_10, _toDisplayString(formatSubText(item)), 1)
                                  ])
                                ], 8, _hoisted_6))
                              }), 128))
                            : (searchHistory.value.length > 0 && !query.value)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                  _createElementVNode("div", _hoisted_11, [
                                    _cache[3] || (_cache[3] = _createElementVNode("span", null, "Recenti", -1)),
                                    _createElementVNode("button", {
                                      onClick: _withModifiers(clearHistory, ["stop"]),
                                      class: "clear-history-btn"
                                    }, " Cancella tutto ")
                                  ]),
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(searchHistory.value, (item, index) => {
                                    return (_openBlock(), _createElementBlock("div", {
                                      key: 'hist-' + index,
                                      class: "suggestion-item history"
                                    }, [
                                      _createElementVNode("div", {
                                        class: "history-content",
                                        onClick: ($event: any) => (selectHistoryItem(item))
                                      }, [
                                        _createElementVNode("div", _hoisted_13, [
                                          _createVNode(_unref(History), { size: 18 })
                                        ]),
                                        _createElementVNode("div", _hoisted_14, [
                                          _createElementVNode("span", _hoisted_15, _toDisplayString(item.display_name.split(",")[0]), 1),
                                          _createElementVNode("span", _hoisted_16, _toDisplayString(item.display_name), 1)
                                        ])
                                      ], 8, _hoisted_12),
                                      _createElementVNode("button", {
                                        class: "remove-item-btn",
                                        onClick: _withModifiers(($event: any) => (removeFromHistory(index)), ["stop"]),
                                        title: "Rimuovi"
                                      }, [
                                        _createVNode(_unref(X), { size: 14 })
                                      ], 8, _hoisted_17)
                                    ]))
                                  }), 128))
                                ], 64))
                              : _createCommentVNode("", true)
                      ]))
                    : _createCommentVNode("", true)
                ])
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]))
  ]))
}
}

})